import { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import SEOHead from '../../components/feature/SEOHead';
import HeroBackground from '../../components/feature/HeroBackground';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://example.com';

const directorySchema = [
  {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    '@id': `${SITE_URL}/india-supplier-directory`,
    url: `${SITE_URL}/india-supplier-directory`,
    name: 'India Supplier Directory – Best Websites to Find Verified Manufacturers in India | True Passage',
    description: 'Comprehensive directory of the best platforms to discover manufacturers and suppliers across India.',
    breadcrumb: {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE_URL}/` },
        { '@type': 'ListItem', position: 2, name: 'Resources', item: `${SITE_URL}/resources` },
        { '@type': 'ListItem', position: 3, name: 'India Supplier Directory', item: `${SITE_URL}/india-supplier-directory` },
      ],
    },
  },
  {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: 'Best Platforms to Find Indian Manufacturers',
    description: 'Curated list of platforms and resources used by global buyers to discover Indian manufacturers',
    numberOfItems: 11,
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'IndiaMART', url: 'https://www.indiamart.com' },
      { '@type': 'ListItem', position: 2, name: 'TradeIndia', url: 'https://www.tradeindia.com' },
      { '@type': 'ListItem', position: 3, name: 'ExportersIndia', url: 'https://www.exportersindia.com' },
      { '@type': 'ListItem', position: 4, name: 'GlobalSources', url: 'https://www.globalsources.com' },
      { '@type': 'ListItem', position: 5, name: 'Alibaba', url: 'https://www.alibaba.com' },
      { '@type': 'ListItem', position: 6, name: 'IndustryNet', url: 'https://www.industrynet.com' },
      { '@type': 'ListItem', position: 7, name: 'Thomasnet', url: 'https://www.thomasnet.com' },
      { '@type': 'ListItem', position: 8, name: 'Engineering360', url: 'https://www.globalspec.com' },
      { '@type': 'ListItem', position: 9, name: 'ACMA Member Directory', url: 'https://www.acma.in' },
      { '@type': 'ListItem', position: 10, name: 'Engineering Export Promotion Council', url: 'https://www.eepcindia.org' },
      { '@type': 'ListItem', position: 11, name: 'Pharmaceutical Export Promotion Council', url: 'https://www.pharmexcil.com' },
    ],
  },
];

interface Supplier {
  name: string;
  description: string;
  website: string | null;
  logo: string | null;
  fallbackIcon: string;
}

// Proper component so hooks work correctly
const SupplierCard = ({ supplier, index }: { supplier: Supplier; index: number }) => {
  const [logoError, setLogoError] = useState(false);

  return (
    <motion.div
      className="bg-white border-2 border-navy-primary/10 rounded-2xl p-8 hover:border-gold-accent hover:shadow-lg transition-all"
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6, delay: index * 0.05 }}
    >
      <div className="flex items-start justify-between mb-5">
        <div className="h-12 flex items-center">
          {supplier.logo && !logoError ? (
            <img
              src={supplier.logo}
              alt={`${supplier.name} logo`}
              className="h-9 max-w-[140px] object-contain"
              onError={() => setLogoError(true)}
            />
          ) : (
            <div className="w-12 h-12 bg-gold-accent/10 rounded-xl flex items-center justify-center">
              <i className={`${supplier.fallbackIcon} text-2xl text-gold-accent`}></i>
            </div>
          )}
        </div>

        {supplier.website && (
          <a
            href={supplier.website}
            target="_blank"
            rel="noopener noreferrer nofollow"
            className="w-10 h-10 bg-gold-accent/10 rounded-lg flex items-center justify-center hover:bg-gold-accent/20 transition-colors flex-shrink-0 cursor-pointer"
          >
            <i className="ri-external-link-line text-gold-accent"></i>
          </a>
        )}
      </div>

      <h3 className="text-xl font-bold text-navy-primary mb-3">{supplier.name}</h3>

      <p className="text-navy-primary/70 leading-relaxed text-sm">{supplier.description}</p>

      {supplier.website && (
        <div className="mt-4 pt-4 border-t border-navy-primary/10">
          <a
            href={supplier.website}
            target="_blank"
            rel="noopener noreferrer nofollow"
            className="text-sm text-gold-accent hover:underline break-all cursor-pointer"
          >
            {supplier.website}
          </a>
        </div>
      )}
    </motion.div>
  );
};

const industrialSuppliers: Supplier[] = [
  {
    name: 'IndiaMART',
    description: 'One of the largest B2B marketplaces in India connecting buyers with manufacturers and distributors.',
    website: 'https://www.indiamart.com',
    logo: 'https://logo.clearbit.com/indiamart.com',
    fallbackIcon: 'ri-store-3-line',
  },
  {
    name: 'TradeIndia',
    description: 'A large directory of Indian exporters and manufacturers across many sectors.',
    website: 'https://www.tradeindia.com',
    logo: 'https://logo.clearbit.com/tradeindia.com',
    fallbackIcon: 'ri-global-line',
  },
  {
    name: 'ExportersIndia',
    description: 'Platform focused on export-oriented manufacturers and global trade.',
    website: 'https://www.exportersindia.com',
    logo: 'https://logo.clearbit.com/exportersindia.com',
    fallbackIcon: 'ri-ship-line',
  },
];

const machiningSuppliers: Supplier[] = [
  {
    name: 'GlobalSources',
    description: 'International sourcing platform connecting buyers with manufacturers across Asia including India.',
    website: 'https://www.globalsources.com',
    logo: 'https://logo.clearbit.com/globalsources.com',
    fallbackIcon: 'ri-earth-line',
  },
  {
    name: 'Alibaba',
    description: 'Global B2B platform where many Indian manufacturers list export-ready products.',
    website: 'https://www.alibaba.com',
    logo: 'https://logo.clearbit.com/alibaba.com',
    fallbackIcon: 'ri-shopping-bag-3-line',
  },
  {
    name: 'IndustryNet',
    description: 'Industrial supplier database with manufacturers and OEM suppliers.',
    website: 'https://www.industrynet.com',
    logo: 'https://logo.clearbit.com/industrynet.com',
    fallbackIcon: 'ri-building-4-line',
  },
];

const toolingSuppliers: Supplier[] = [
  {
    name: 'Thomasnet',
    description: 'One of the largest industrial supplier directories globally.',
    website: 'https://www.thomasnet.com',
    logo: 'https://logo.clearbit.com/thomasnet.com',
    fallbackIcon: 'ri-tools-line',
  },
  {
    name: 'Engineering360',
    description: 'Directory of engineering suppliers and manufacturers.',
    website: 'https://www.globalspec.com',
    logo: 'https://logo.clearbit.com/globalspec.com',
    fallbackIcon: 'ri-settings-4-line',
  },
];

const automotiveSuppliers: Supplier[] = [
  {
    name: 'ACMA Member Directory',
    description: 'Association of Automotive Component Manufacturers of India.',
    website: 'https://www.acma.in',
    logo: 'https://logo.clearbit.com/acma.in',
    fallbackIcon: 'ri-car-line',
  },
  {
    name: 'AutoComponent India',
    description: 'Platform connecting automotive parts manufacturers and exporters.',
    website: null,
    logo: null,
    fallbackIcon: 'ri-steering-2-line',
  },
];

const governmentDirectories: Supplier[] = [
  {
    name: 'Engineering Export Promotion Council',
    description: 'Industry-specific organization maintaining directories of verified exporters.',
    website: 'https://www.eepcindia.org',
    logo: 'https://logo.clearbit.com/eepcindia.org',
    fallbackIcon: 'ri-government-line',
  },
  {
    name: 'Pharmaceutical Export Promotion Council',
    description: 'Official directory of pharmaceutical exporters in India.',
    website: 'https://www.pharmexcil.com',
    logo: 'https://logo.clearbit.com/pharmexcil.com',
    fallbackIcon: 'ri-capsule-line',
  },
];

const challenges = [
  { title: 'Supplier Credibility', description: 'Verifying the legitimacy and track record of manufacturers.', icon: 'ri-shield-check-line' },
  { title: 'Factory Capability Verification', description: 'Assessing actual production capacity and technical capabilities.', icon: 'ri-factory-line' },
  { title: 'Quality Assurance', description: 'Ensuring consistent product quality and manufacturing standards.', icon: 'ri-medal-line' },
  { title: 'Production Coordination', description: 'Managing timelines, specifications, and communication during production.', icon: 'ri-time-line' },
  { title: 'Logistics and Export Management', description: 'Handling shipping, customs, and international logistics complexities.', icon: 'ri-truck-line' },
];

const services = [
  { title: 'Supplier Identification', icon: 'ri-search-line', description: 'Find the right manufacturers for your specific needs.' },
  { title: 'Factory Vetting and Verification', icon: 'ri-shield-check-line', description: 'Comprehensive assessment of manufacturing capabilities.' },
  { title: 'Production Coordination', icon: 'ri-calendar-check-line', description: 'Manage timelines and specifications throughout production.' },
  { title: 'Quality Control', icon: 'ri-medal-line', description: 'Ensure consistent quality standards and product specifications.' },
  { title: 'Procurement Support', icon: 'ri-shopping-cart-line', description: 'End-to-end procurement management and coordination.' },
  { title: 'Logistics Management', icon: 'ri-ship-line', description: 'Handle shipping, customs, and international logistics.' },
];

const SectionHeader = ({ title, subtitle }: { title: string; subtitle?: string }) => (
  <motion.div
    className="mb-12"
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ duration: 0.6 }}
  >
    <h2 className="text-4xl font-bold text-navy-primary mb-4">{title}</h2>
    {subtitle && <p className="text-lg text-navy-primary/70 max-w-3xl">{subtitle}</p>}
  </motion.div>
);

const IndiaSupplierDirectoryPage = () => (
  <div className="min-h-screen bg-white">
    <SEOHead
      title="India Supplier Directory – Best Websites to Find Verified Manufacturers in India | True Passage"
      description="Comprehensive directory of the best platforms to discover manufacturers and suppliers across India. Find industrial suppliers, machining vendors, OEM manufacturers, and automotive component suppliers."
      keywords="India supplier directory, manufacturers in India, Indian suppliers list, industrial suppliers India, machining suppliers India, OEM manufacturers India, find suppliers in India, verified manufacturers India, B2B India"
      canonical="/india-supplier-directory"
      schema={directorySchema}
    />
    <Navbar />

    {/* Hero Section */}
    <section className="relative pt-32 pb-20 bg-gradient-to-br from-navy-primary via-navy-secondary to-navy-primary overflow-hidden">
      <HeroBackground />
      <div className="relative z-10 max-w-[1400px] mx-auto px-6 lg:px-12">
        <motion.div
          className="text-center max-w-4xl mx-auto"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <h1 className="text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
            India Supplier <span className="text-gold-accent">Directory</span>
          </h1>
          <p className="text-xl text-white/80 mb-10 leading-relaxed">
            Best Websites to Find Verified Manufacturers in India
          </p>
          <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20">
            <p className="text-lg font-semibold text-white mb-3">
              Finding suppliers is easy. Finding reliable ones is not.
            </p>
            <p className="text-base text-white/70 mb-8">
              True Passage Ventures helps you identify, verify, and work with the right manufacturers across India.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/request-quote"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-gold-accent text-navy-primary rounded-full font-semibold text-base hover:scale-105 transition-transform shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
              >
                Start Sourcing With Us
                <i className="ri-arrow-right-line"></i>
              </Link>
              <a
                href="#supplier-platforms"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white/10 text-white border-2 border-white/20 rounded-full font-semibold text-base hover:bg-white/20 transition-all whitespace-nowrap cursor-pointer"
              >
                View Supplier Platforms
                <i className="ri-arrow-down-line"></i>
              </a>
            </div>
          </div>
        </motion.div>
      </div>
    </section>

    {/* Introduction */}
    <section id="supplier-platforms" className="py-20 bg-neutral-light">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
        <motion.div
          className="text-center mb-4"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="text-4xl font-bold text-navy-primary mb-4">Where to Find Suppliers in India</h2>
          <p className="text-lg text-navy-primary/70 max-w-3xl mx-auto leading-relaxed">
            A curated list of platforms used by global buyers to discover Indian manufacturers across different industries.
          </p>
        </motion.div>
      </div>
    </section>

    {/* Industrial Supply */}
    <section className="py-20">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
        <SectionHeader
          title="Industrial Supply Suppliers"
          subtitle="These platforms list manufacturers for industrial components, mechanical parts, and engineering products."
        />
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {industrialSuppliers.map((s, i) => <SupplierCard key={s.name} supplier={s} index={i} />)}
        </div>
      </div>
    </section>

    {/* Machining & Manufacturing */}
    <section className="py-20 bg-neutral-light">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
        <SectionHeader
          title="Machining and Manufacturing Suppliers"
          subtitle="Platforms where buyers can find CNC machining vendors, precision manufacturers, and OEM component producers."
        />
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {machiningSuppliers.map((s, i) => <SupplierCard key={s.name} supplier={s} index={i} />)}
        </div>
      </div>
    </section>

    {/* Tooling & Engineering */}
    <section className="py-20">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
        <SectionHeader
          title="Tooling and Engineering Suppliers"
          subtitle="Directories and marketplaces focused on tooling manufacturers and industrial engineering suppliers."
        />
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {toolingSuppliers.map((s, i) => <SupplierCard key={s.name} supplier={s} index={i} />)}
        </div>
      </div>
    </section>

    {/* Automotive */}
    <section className="py-20 bg-neutral-light">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
        <SectionHeader
          title="Automotive Component Suppliers"
          subtitle="India has a large automotive manufacturing ecosystem. These platforms help locate component suppliers and OEM manufacturers."
        />
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {automotiveSuppliers.map((s, i) => <SupplierCard key={s.name} supplier={s} index={i} />)}
        </div>
      </div>
    </section>

    {/* Government Export Directories */}
    <section className="py-20">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
        <SectionHeader
          title="Government Export Directories"
          subtitle="Official resources listing certified exporters in India, maintained by Export Promotion Councils."
        />
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {governmentDirectories.map((s, i) => <SupplierCard key={s.name} supplier={s} index={i} />)}
        </div>
      </div>
    </section>

    {/* The Challenge */}
    <section className="py-20 bg-neutral-light">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="text-4xl font-bold text-navy-primary mb-4">The Challenge With Marketplaces</h2>
          <p className="text-lg text-navy-primary/70 max-w-3xl mx-auto leading-relaxed">
            These platforms help identify suppliers — but they don't solve the hard problems buyers face during sourcing.
          </p>
        </motion.div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {challenges.map((c, i) => (
            <motion.div
              key={c.title}
              className="bg-white border-2 border-navy-primary/10 rounded-2xl p-8 hover:border-gold-accent hover:shadow-lg transition-all"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: i * 0.1 }}
            >
              <div className="w-14 h-14 bg-gold-accent/10 rounded-xl flex items-center justify-center mb-6">
                <i className={`${c.icon} text-2xl text-gold-accent`}></i>
              </div>
              <h3 className="text-xl font-bold text-navy-primary mb-3">{c.title}</h3>
              <p className="text-navy-primary/70 leading-relaxed">{c.description}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>

    {/* How True Passage Helps */}
    <section className="py-20">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="text-4xl font-bold text-navy-primary mb-4">How True Passage Helps</h2>
          <p className="text-lg text-navy-primary/70 max-w-3xl mx-auto">
            We help global buyers move from supplier discovery to reliable, verified sourcing.
          </p>
        </motion.div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
          {services.map((s, i) => (
            <motion.div
              key={s.title}
              className="bg-white border-2 border-navy-primary/10 rounded-2xl p-8 hover:border-gold-accent hover:shadow-lg transition-all"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: i * 0.1 }}
            >
              <div className="w-14 h-14 bg-gold-accent/10 rounded-xl flex items-center justify-center mb-6">
                <i className={`${s.icon} text-2xl text-gold-accent`}></i>
              </div>
              <h3 className="text-xl font-bold text-navy-primary mb-3">{s.title}</h3>
              <p className="text-navy-primary/70 leading-relaxed">{s.description}</p>
            </motion.div>
          ))}
        </div>
        <motion.div
          className="text-center bg-navy-primary/5 rounded-2xl p-8"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <p className="text-lg text-navy-primary">
            Whether you already identified a supplier or need help finding one, we manage the entire process.
          </p>
        </motion.div>
      </div>
    </section>

    {/* CTA Section */}
    <section className="py-20 bg-neutral-light">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
        <motion.div
          className="bg-gradient-to-br from-navy-primary to-navy-secondary rounded-3xl p-12 lg:p-16 text-center"
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <h2 className="text-4xl font-bold text-white mb-4">Ready to Source from India?</h2>
          <p className="text-lg text-white/70 mb-8 max-w-xl mx-auto">
            Work with a partner who knows India's supplier networks. Get started with True Passage Ventures.
          </p>
          <Link
            to="/request-quote"
            className="inline-flex items-center gap-2 px-8 py-4 bg-gold-accent text-navy-primary rounded-full font-semibold text-base hover:scale-105 transition-transform shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
          >
            Get a Sourcing Quote
            <i className="ri-arrow-right-line"></i>
          </Link>
        </motion.div>
      </div>
    </section>

    <Footer />
  </div>
);

export default IndiaSupplierDirectoryPage;
